import { useShallow } from "zustand/react/shallow";
import { isAiResidentActorId, type PersonWorld } from "@townsy/contracts";
import { usePersonWorldQuery } from "../../api/worlds";
import { useTownSession } from "../../app/TownSessionContext";
import { useTownStore, type WorldSwitchUi } from "../../state/store";

/**
 * "Join them in Finch" (ADR-0043 decision 6) on a person card and in a private conversation. Shown only when the SERVER
 * says which world that person is in (their privacy setting allows this viewer); otherwise nothing at all is shown, so
 * the card looks the same for a hidden person as for one who is offline. The join itself goes through the current room
 * (`world.switch`), which checks the setting again.
 */
export function JoinFriendButton({ actorId }: { actorId: string }) {
  const session = useTownSession();
  const ai = isAiResidentActorId(actorId);
  // Someone this visitor muted is never offered (the server hides them from an account's mutes; a guest's are local).
  const muted = useTownStore((s) => actorId in s.mutes);
  const q = usePersonWorldQuery(session && !ai && !muted ? actorId : null);
  const { myCopy, inside, joined, switching } = useTownStore(
    useShallow((s) => ({
      myCopy: s.room.worldCopy,
      inside: s.room.kind === "interior",
      joined: s.room.status === "joined" && !s.ui.transitionPending,
      switching: s.ui.worldSwitch,
    })),
  );
  if (!session || ai || muted) return null;
  return (
    <JoinFriendView
      world={q.data?.world ?? null}
      myCopy={myCopy}
      inside={inside}
      canSwitch={joined}
      switching={switching}
      friendActorId={actorId}
      onJoin={() => session.joinFriend(actorId)}
    />
  );
}

export interface JoinFriendViewProps {
  /** What the server showed (null: nothing to show). */
  world: PersonWorld | null;
  myCopy: number | null;
  inside: boolean;
  canSwitch: boolean;
  switching: WorldSwitchUi;
  friendActorId: string;
  onJoin(): void;
}

/** Presentational (server-rendered in the unit tests). */
export function JoinFriendView({ world, myCopy, inside, canSwitch, switching, friendActorId, onJoin }: JoinFriendViewProps) {
  if (!world) return null;
  const mine = switching.status === "pending" && switching.friendActorId === friendActorId;
  const refused = switching.status === "refused" && (switching.copy === world.copy || switching.copy === null) ? switching.message : null;
  // On the street the same copy means "here already"; inside a building everyone shares the room, so joining their copy
  // (out of this door) still means something unless it is the copy the visitor came from.
  const same = world.copy === myCopy;
  if (same) {
    return (
      <p className="hint" style={{ margin: 0 }} data-testid="join-friend-same">
        {inside ? `You both came from ${world.name}.` : `In your world (${world.name}).`}
      </p>
    );
  }
  return (
    <div className="join-friend" data-testid="join-friend">
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={!canSwitch || switching.status === "pending"}
        onClick={onJoin}
        title={inside ? `Leave by this door into ${world.name}` : `Go to ${world.name}: you arrive on its square`}
        data-testid="join-friend-button"
      >
        {mine ? "Joining…" : `Join them in ${world.name}`}
      </button>
      {refused && !mine ? (
        <p className="small error-text" role="alert" style={{ margin: 0 }} data-testid="join-friend-refused">
          {refused}
        </p>
      ) : null}
    </div>
  );
}
